import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useState } from "react";
import { modalImportAnswers } from "../../../atoms/atom";
import { FormService } from "../../../services/FormService";
import { FormDTO } from "../../../types/FormDTO";
import { useAlert } from "../../contextProviders/AlertProvider";
import '../info-form-modal/InfoFormModal.scss';

const ImportAnswersModal: FC<Partial<FormDTO>> = (dto: Partial<FormDTO>) => {
	const [open, setOpen] = useAtom(modalImportAnswers);
	const { showAlert, showError } = useAlert();
	const [loadingModelo, setLoadingModelo] = useState(false);
	const [loadingImport, setLoadingImport] = useState(false);
	const [uploadedFile, setUploadedFile] = useState<FormData | null>(null);
	const service = new FormService();
	const fileName = uploadedFile
		? (() => {
			const entry = uploadedFile.get("file");
			if (entry instanceof File) {
				return entry.name;
			}
			return "Nenhum arquivo";
		})()
		: "Nenhum arquivo";

	const handleDownloadModelo = async () => {
		setLoadingModelo(true);
		if (!dto.id) return;
		try {
			const response = await service.downloadModel();
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `Modelo para importação de respostas.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error("Erro ao baixar arquivo: ", err);
			showError();
		} finally {
			setLoadingModelo(false);
		}
	};

	const closeDialog = () => {
		setUploadedFile(null);
		setOpen(false);
	}

	return (
		<Dialog
			open={open}
			onClose={() => closeDialog()}
			id='modal-info'
		>
			<DialogTitle>Importar respostas</DialogTitle>
			<DialogContent id="modal-info-content">
				<Typography variant="h6" sx={{width: "400px"}}>
					Realize a importação das respostas para o formulário através de um arquivo Excel no
					formato correto. As respostas do arquivo escolhido serão incluídas neste formulário.
					Garanta que o arquivo escolhido esteja com as mesmas colunas do modelo disponibilizado.
				</Typography>
				<div id="form-info-buttons">
					<Button
						variant="contained"
						color="info"
						onClick={handleDownloadModelo}
						sx={{width: "380px", height: "50px"}}
					>
						{loadingModelo
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Baixar modelo de planilha para respostas"
						}
					</Button>
					{uploadedFile
						? <Typography variant="h6" sx={{width: "38vh"}}>Arquivo selecionado: {fileName}</Typography>
						: <Button
							variant="contained"
							color="primary"
							component="label"
							sx={{ width: "380px", height: "50px" }}
						>
							Escolher arquivo com respostas (.xlsx)
							<input
								hidden
								type="file"
								accept=".xlsx,.xls"
								onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
									const file = e.target.files?.[0];
									if (!file) return;
									if (!dto.id) {
										showError();
										return;
									}
									const formData = new FormData();
									formData.append("file", file);
									setUploadedFile(formData);
								}}
							/>
						</Button>
					}
				</div>
			</DialogContent>
			<DialogActions>
				<Button id="close-form-info" onClick={() => closeDialog()}>
					Fechar
				</Button>
				{uploadedFile && (
					<Button
						variant="contained"
						color="success"
						onClick={() => importAnswers()}
						sx={{width: "100px"}}
						disabled={loadingImport}
					>
						{loadingImport
							? <CircularProgress sx={{color: "#292929ff"}} size={'25px'}/>
							: "Importar"
						}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);

	async function importAnswers() {
		setLoadingImport(true);
		if (!uploadedFile || !dto.id) return;
		try {
			await service.importAnswers(dto.id, uploadedFile);
			closeDialog();
			showAlert("Respostas importadas com sucesso!");
		} catch (err) {
			console.error("Erro ao importar respostas: ", err);
			showError();
		} finally {
			setLoadingImport(false);
		}
	}
}

export default ImportAnswersModal;